/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/Components/AzureSubscriptionAuthComponent" />
import * as React from "react";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { FormItem } from "VSSUI/FormItem";
import * as Utils_String from "VSS/Core/Util/String";
import { ServiceEndpoint, AzureSubscription } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { SubscriptionPicker, SubscriptionConsumer, ISubscriptionContextValue, Subscription, SubscriptionProvider } from "DistributedTask/Subscriptions/SubscriptionPicker";
import { AzureSubscriptionAuthActionCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/Actions/AzureSubscriptionAuthActionCreator";
import { IAzureSubscriptionAuthStoreState, AzureSubscriptionAuthStore } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/Store/AzureSubscriptionAuthStore";
import { IKubernetesCluster } from "DistributedTask/Common/AzureResourcesService/AzureResourceContracts";
import { KubernetesEndpointEditorInstanceId } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/KubernetesEndpointConstants";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { IEndpointEditorComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown } from "VSSUI/Dropdown";
import { IListBoxItem } from "VSSUI/ListBox";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

export interface IAzureSubscriptionAuthComponentProps extends IEndpointEditorComponentProps {
    updateValidAndDirtyStateChange: (isValid?: boolean, isDirty?: boolean) => void;
}

export interface IAzureSubscriptionAuthComponentState extends IAzureSubscriptionAuthStoreState {
    selectedSubscription: AzureSubscription;
    selectedCluster: IKubernetesCluster;
    selectedNamespace: string;
    isComponentValid: boolean;
}

export class AzureSubscriptionAuthComponent extends VssComponent<IAzureSubscriptionAuthComponentProps, IAzureSubscriptionAuthComponentState>{

    constructor(props: IAzureSubscriptionAuthComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._draftServiceEndpoint = Object.assign({}, this.props.draftEndpoint);
        this._draftServiceEndpoint.data = this._draftServiceEndpoint.data || {};
        this._draftServiceEndpoint.authorization.parameters = this._draftServiceEndpoint.authorization.parameters || {};
        this._actionCreator = ActionCreatorManager.GetActionCreator<AzureSubscriptionAuthActionCreator>(AzureSubscriptionAuthActionCreator, KubernetesEndpointEditorInstanceId);
        this._store = StoreManager.GetStore<AzureSubscriptionAuthStore>(AzureSubscriptionAuthStore, KubernetesEndpointEditorInstanceId);
        this._store.addChangedListener(this._onStoreChange);
        this._isComponentValid = this.props.isUpdateForm ? true : false;
        if (!this._isComponentValid) {
            this.props.updateValidAndDirtyStateChange(false);
        }
        this._initializeState();
    }

    public componentWillReceiveProps(props: IAzureSubscriptionAuthComponentProps) {
        this._draftServiceEndpoint = Object.assign({}, props.draftEndpoint);
    }

    public componentWillUnmount() {
        if (!this.state.isComponentValid) {
            this.props.updateValidAndDirtyStateChange(true);
        }

        this._store.removeChangedListener(this._onStoreChange);
    }

    public render(): React.ReactNode {
        return this.props.isUpdateForm ? this._getUpdateForm() : this._getCreateForm();
    }

    private _getUpdateForm(): React.ReactNode {
        return (
            <React.Fragment>
                <FormItem
                    label={Resources.AzureSubscription}
                    className="default-input-margin-top">
                    <TextField
                        value={this.state.selectedSubscription && this.state.selectedSubscription.displayName + " (" + this.state.selectedSubscription.subscriptionId + ")"}
                        disabled={true}
                    />
                </FormItem>
                <FormItem
                    label={Resources.Cluster}
                    className="default-input-margin-top">
                    <TextField
                        value={this.state.selectedCluster && this.state.selectedCluster.name + " (" + this.state.selectedCluster.resourceGroup + ")"}
                        disabled={true}
                    />
                </FormItem>
                <FormItem
                    label={Resources.Namespace}
                    className="default-input-margin-top">
                    <TextField
                        value={this.state.selectedNamespace}
                        disabled={true}
                    />
                </FormItem>
                <Checkbox
                    className="default-input-margin-top"
                    checked={this._draftServiceEndpoint.data["clusterAdmin"] === "true"}
                    label={Resources.UseClusterAdminCredentials}
                    disabled={true}
                />
                <Checkbox
                    className="default-input-margin-top"
                    onChange={this._onRenewServiceAccountCheckToggle}
                    checked={this._draftServiceEndpoint.data["operation.type"] === "update"}
                    label={Resources.CreateNewServiceAccount}
                />
            </React.Fragment>
        );
    }

    private _getCreateForm(): React.ReactNode {
        return (
            <React.Fragment>
                <SubscriptionProvider pageContext={this.context.pageContext}>
                    <SubscriptionConsumer>
                        {(subscriptionContext: ISubscriptionContextValue) => {
                            const isLoadComplete = subscriptionContext.state && subscriptionContext.state.loadedAllSubscriptions;
                            const subscriptions: Subscription[] | undefined = isLoadComplete ? subscriptionContext.state.subscriptions : undefined;
                            if (!this.state.selectedSubscription && subscriptions && subscriptions[0]) {
                                setTimeout(() => {
                                    this._onSubscriptionChanged(subscriptions[0]);
                                });
                            }

                            return (
                                <SubscriptionPicker
                                    useDropdown={true}
                                    label={Resources.AzureSubscription}
                                    showCreateSubscriptionButton={false}
                                    subscriptionContext={subscriptionContext}
                                    onSubscriptionChoiceChanged={this._onSubscriptionChanged}
                                    selectedSubscriptionId={this.state.selectedSubscription && this.state.selectedSubscription.subscriptionId || ""}
                                    className="default-input-margin-top"
                                />);
                        }}
                    </SubscriptionConsumer>
                </SubscriptionProvider>
                {this.state.selectedSubscription &&
                    (this.state.aksClustersList.length ?
                        <React.Fragment>
                            <div className="default-input-margin-top">
                                {Resources.Cluster}
                            </div>
                            <Dropdown
                                ariaLabel={Resources.Cluster}
                                items={this._getClusterOptions()}
                                onSelect={this._onClusterChanged}
                                selection={this._getClusterSelection()}
                                filterThrottleWait={200}
                            />
                        </React.Fragment>
                        : (this.state.fetchingMoreClusters ?
                            <Spinner
                                ariaLive="assertive"
                                ariaLabel={Resources.LoadingClusters}
                                label={Resources.LoadingClusters}
                                size={SpinnerSize.medium}
                                className="default-input-margin-top"
                            />
                            : <span className="default-input-margin-top">{Resources.NoClustersMessage}</span>
                        ))
                }
                {this.state.selectedCluster &&
                    (this.state.namespacesList.length ?
                        <FormItem
                            label={Resources.Namespace}
                            className="default-input-margin-top"
                        >
                            <Dropdown
                                ariaLabel={Resources.Namespace}
                                items={this._getNamespaceOptions()}
                                onSelect={this._onNamespaceChanged}
                                selection={this._getNamespaceSelection()}
                            />
                            <Checkbox
                                className="default-input-margin-top"
                                onChange={this._onUseClusterAdminToggle}
                                checked={this._draftServiceEndpoint.data["clusterAdmin"] === "true"}
                                label={Resources.UseClusterAdminCredentials}
                            />
                        </FormItem>
                        : (this.state.fetchingMoreNamespaces ?
                            <Spinner
                                ariaLive="assertive"
                                ariaLabel={Resources.LoadingNamespaces}
                                label={Resources.LoadingNamespaces}
                                size={SpinnerSize.medium}
                                className="default-input-margin-top"
                            />
                            : <span className="default-input-margin-top">{Resources.NoNamespacesMessage}</span>
                        ))
                }
            </React.Fragment>
        );
    }

    private _onSubscriptionChanged = (subscription: Subscription | undefined): void => {
        const azureSubscription = subscription
            ? {
                displayName: subscription.displayName,
                subscriptionId: subscription.id,
                subscriptionTenantId: subscription.subscriptionAccount.subscriptionTenantId,
            } as AzureSubscription
            : undefined;

        if (azureSubscription) {
            this.setState({
                selectedSubscription: azureSubscription,
                selectedCluster: undefined,
                selectedNamespace: undefined,
                fetchingMoreClusters: true,
                isComponentValid: false
            }, this._checkAndUpdatePanelState);

            this._actionCreator.getAccessToken(azureSubscription.subscriptionTenantId);
            this._actionCreator.getAksClusters(azureSubscription);
        }
    }

    private _getClusterOptions(): IListBoxItem[] {
        let items: IListBoxItem[] = [];
        let aksClustersList = this.state.aksClustersList;
        if (aksClustersList) {
            aksClustersList.forEach(cluster => {
                items.push({
                    id: cluster.id,
                    text: cluster.resourceGroup ? Utils_String.format("{0} ({1})", cluster.name, cluster.resourceGroup) : cluster.name,
                    data: cluster
                });
            });
        }

        return items;
    }

    private _onClusterChanged = (event: React.SyntheticEvent<HTMLElement>, selectedOption: IListBoxItem): void => {
        let clustersList = this.state.aksClustersList;
        const cluster: IKubernetesCluster = clustersList.find((cluster) => { return cluster.id === selectedOption.id; });
        this.setState({
            selectedCluster: cluster,
            serverUrl: this._getServerUrlFromCluster(cluster),
            namespacesList: [],
            selectedNamespace: "",
            isComponentValid: false,
            fetchingMoreNamespaces: true
        }, this._checkAndUpdatePanelState);

        this._actionCreator.getKubernetesClusterNamespaces(this.state.selectedSubscription, cluster.id, cluster.fqdn);
    }

    private _getClusterSelection(): DropdownSelection {
        let clusterDropdownSelection = new DropdownSelection();
        if (this.state.selectedCluster && this.state.selectedCluster.id) {
            const selectedClusterIndex = this.state.aksClustersList.find((cluster) => { return cluster.id === this.state.selectedCluster.id; });
            if (selectedClusterIndex >= 0) {
                clusterDropdownSelection.select(selectedClusterIndex);
                return clusterDropdownSelection;
            }
        }

        return undefined;
    }

    private _checkAndUpdatePanelState = () => {
        if (this.state.isComponentValid !== this._isComponentValid) {
            this._isComponentValid = this.state.isComponentValid;
            this.props.updateValidAndDirtyStateChange(this.state.isComponentValid);
        }
    }

    private _getNamespaceOptions(): IListBoxItem[] {
        let items: IListBoxItem[] = [];
        if (!!this.state.selectedCluster) {
            this.state.namespacesList.forEach(namespace => {
                items.push({ id: namespace, text: namespace, data: namespace });
            });
        }

        return items;
    }

    private _onNamespaceChanged = (event: React.SyntheticEvent<HTMLElement>, selectedOption: IListBoxItem): void => {
        let namespace = selectedOption.id;
        this.setState({ selectedNamespace: namespace, isComponentValid: true }, () => {
            this._updateDraftEndpointState();
            this._checkAndUpdatePanelState();
        });
    }

    private _getNamespaceSelection(): DropdownSelection {
        let namespaceDropdownSelection = new DropdownSelection();
        if (this.state.selectedNamespace) {
            const selectedNamespaceIndex = this.state.namespacesList.findIndex((namespace) => { return namespace === this.state.selectedNamespace; });
            if (selectedNamespaceIndex >= 0) {
                namespaceDropdownSelection.select(selectedNamespaceIndex);
                return namespaceDropdownSelection;
            }
        }

        return undefined;
    }

    private _onRenewServiceAccountCheckToggle = (event: any, checked: boolean): void => {
        this._draftServiceEndpoint.data["operation.type"] = checked ? "update" : undefined;
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        const isDirty = checked ? true : false;
        this.props.updateValidAndDirtyStateChange(undefined, isDirty);
    }

    private _onUseClusterAdminToggle = (event: any, checked: boolean): void => {
        this._draftServiceEndpoint.data["clusterAdmin"] = checked.toString();
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        this.props.updateValidAndDirtyStateChange(undefined, checked);
    }

    private _onStoreChange = (): void => {
        let storeState = this._store.getState();
        this.setState({
            aksClustersList: storeState.aksClustersList,
            namespacesList: storeState.namespacesList,
            errorMessage: storeState.errorMessage,
            accessToken: storeState.accessToken,
            fetchingMoreClusters: storeState.fetchingMoreClusters,
            fetchingMoreNamespaces: storeState.fetchingMoreNamespaces
        });
    }

    private _updateDraftEndpointState = () => {
        this._draftServiceEndpoint.url = this.state.serverUrl;
        this._draftServiceEndpoint.data.azureSubscriptionId = this.state.selectedSubscription.subscriptionId;
        this._draftServiceEndpoint.data.azureSubscriptionName = this.state.selectedSubscription.displayName;
        this._draftServiceEndpoint.data.clusterId = this.state.selectedCluster.id;
        this._draftServiceEndpoint.data.namespace = this.state.selectedNamespace;
        this._draftServiceEndpoint.authorization.parameters.azureTenantId = this.state.selectedSubscription.subscriptionTenantId;
        this._draftServiceEndpoint.authorization.parameters.azureAccessToken = this.state.accessToken;
        this._draftServiceEndpoint.authorization.parameters.azureEnvironment = "AzureCloud";

        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
    }

    private _getSubscriptionFromEndpoint = (): AzureSubscription => {
        if (this._draftServiceEndpoint.data.azureSubscriptionId) {
            return ({
                subscriptionId: this._draftServiceEndpoint.data.azureSubscriptionId || "",
                displayName: this._draftServiceEndpoint.data.azureSubscriptionName || "",
                subscriptionTenantId: "",
                subscriptionTenantName: ""
            });
        }

        return undefined;
    }

    private _getClusterFromEndpoint = (): IKubernetesCluster => {
        let cluster: IKubernetesCluster = undefined;

        if (this._draftServiceEndpoint.data.clusterId) {
            let clusterId = this._draftServiceEndpoint.data.clusterId;
            let clusterName = "";
            let resourceGroup = "";

            let parts = clusterId.split("/");
            let clusterNameIndex = parts.findIndex(part => Utils_String.equals(part, "managedClusters", true)) + 1;
            if (clusterNameIndex > 0) {
                clusterName = parts[clusterNameIndex];
            }

            let resourceGroupIndex = parts.findIndex(part => Utils_String.equals(part, "resourcegroups", true)) + 1;
            if (resourceGroupIndex > 0) {
                resourceGroup = parts[resourceGroupIndex];
            }

            cluster = {
                id: clusterId,
                name: clusterName,
                fqdn: "",
                resourceGroup: resourceGroup
            };
        }

        return cluster;
    }

    private _getServerUrlFromCluster = (cluster: IKubernetesCluster): string => {
        if (!cluster || !cluster.fqdn) {
            throw Resources.KubernetesCouldNotObtainClusterUrl;
        }

        return cluster.fqdn;
    }

    private _initializeState() {
        this.state = {
            selectedSubscription: this._getSubscriptionFromEndpoint(),
            selectedCluster: this._getClusterFromEndpoint(),
            selectedNamespace: this._draftServiceEndpoint.data.namespace || "",
            isComponentValid: this._isComponentValid,
            fetchingMoreClusters: false,
            fetchingMoreNamespaces: false,
            aksClustersList: [],
            namespacesList: [],
            errorMessage: "",
            accessToken: undefined,
            serverUrl: this._draftServiceEndpoint.url || ""
        };
    }

    private _draftServiceEndpoint: ServiceEndpoint;
    private _actionCreator: AzureSubscriptionAuthActionCreator;
    private _store: AzureSubscriptionAuthStore;
    private _isComponentValid: boolean;
}
